import Controller, { inject as controller } from '@ember/controller';
import { action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { inject as service } from '@ember/service';
import { dropTask, enqueueTask } from 'ember-concurrency';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { EMBER_ROUTE_NAMES } from "../../constants/routes";
import { COMPANY_PROFILE_COMPONENT } from 'sh-www/components/company-profile/company-profile';
import { ADMIN_USER_CONTROLLER } from 'sh-www/controllers/admin/users';

class AdminCompaniesController extends Controller {
  @controller application;
  @service api;
  @service router;
  @service shData;
  @service('commands/user-commands') userCommandService;
  @service('state-managers/application') applicationStateManager;
  @service('error-tracker') errorTracker;

  get queryParams() {
    return ['idOrSlug', 'name', 'tier', 'page', 'changeUser', 'onInvoice'];
  }

  idOrSlug = null;
  name = null;
  tier = null;
  groupId = null;
  onInvoice = null;
  changeUser = null;
  page = 1;

  previousPage = null;
  nextPage = null;
  pagination = null;

  user = null;

  /** @type {'user'|'company'|null} */
  profileOverlayType = null;

  /** @type {User|Company|null} */
  profileOverlayModel = null;

  /** @type {Boolean} */
  @computed('previousPage')
  get isFirstPage() {
    return isEmpty(this.previousPage);
  }

  /** @type {Boolean} */
  @computed('nextPage')
  get isLastPage() {
    return isEmpty(this.nextPage);
  }

  /** @type {Boolean} */
  @computed('idOrSlug','name','tier','groupId','onInvoice')
  get hasData() {
    return this.idOrSlug !== null
      || this.name !== null
      || this.tier !== null
      || this.groupId !== null
      || this.onInvoice !== null;
  }

  @dropTask
  *changeUserCompany(userId, companyId){
    try {
      yield this.userCommandService.setUserCompany(userId, companyId, ['editor']);
      yield this.loadUser.perform(userId);
      this.applicationStateManager.addToast('User company successfully changed.');
    } catch (error) {
      handleErrorResponseWithModal(this, error);
    }
  }

  @enqueueTask
  *loadUser(userId){
    const userData = yield this.userCommandService.getUser(userId, ADMIN_USER_CONTROLLER.INCLUDES).catch((error) => {
      handleErrorResponseWithModal(this, error);
    });
    this.set('user', userData);
  }

  @dropTask
  *loadCompanies(targetPage = null) {
    const page = targetPage || this.page;
    const options = { params: { page } };

    ['idOrSlug', 'name', 'tier', 'groupId', 'onInvoice'].forEach((property) => {
      const value = this[property];

      if (value !== null) {
        options.params.filter = options.params.filter || {};
        options.params.filter[property] = value;
      }
    });

    this.shData.addIncludesToOptions(options, ['groups']);

    try {
      const response = yield this.api.request('/companies', options);
      this.shData.pushResponseToStore(response);

      const pagination = this.shData.getPaginationModelFromResponse(response);
      const companies = this.shData.getModelArrayFromResponse(response, 'company');

      const currentPage = response.meta.pagination.current_page;
      const totalPages = response.meta.pagination.total_pages;

      const previousPage = currentPage > 1 ? currentPage - 1 : null;
      const nextPage = currentPage < totalPages ? currentPage + 1 : null;

      this.setProperties({
        page,
        previousPage,
        nextPage,
        pagination,
        model: { companies },
      });
    } catch (error) {
      handleErrorResponseWithModal(this, error);
    }
  }

  /**
   * @param {Object} data
   * @param {String} data.idOrSlug
   * @param {String} data.name
   * @param {String} data.tier
   * @param {String} data.groupId
   */
  @action
  handleSearch({ idOrSlug, name, tier, groupId, onInvoice }) {
    this.setProperties({
      idOrSlug,
      name,
      tier,
      groupId,
      onInvoice,
    });

    this.loadCompanies.perform(1);
  }

  @action
  handleReset() {
    this.setProperties({
      idOrSlug: null,
      name: null,
      tier: null,
      groupId: null,
      onInvoice: null,
      page: 1,
      previousPage: null,
      nextPage: null,
      pagination: null,
      model: {
        companies: null,
      },
    });
  }

  /**
   * @param {String} companyId
   * @param {String} subroute
   */
  @action
  handleEdit(companyId, subroute) {
    // This block of code can replace the switch below once all subroutes are in Ember.
    // const route = subroute ? `admin.company.edit.${subroute}` : 'admin.company.edit';
    // this.router.transitionTo(route, companyId);

    switch (subroute) {
      case undefined:
        this.router.transitionTo(EMBER_ROUTE_NAMES.COMPANY_PROFILE, companyId);
        break;
      case 'members':
        this.router.transitionTo(EMBER_ROUTE_NAMES.COMPANY_PROFILE, companyId, { queryParams: { section: COMPANY_PROFILE_COMPONENT.SECTIONS.MEMBERS }});
        break;
      case 'billing':
        this.router.transitionTo(EMBER_ROUTE_NAMES.COMPANY_PROFILE, companyId, {
          queryParams: { section: 'billing' },
        });
        break;
      case 'legal':
        window.location.assign(`/company/${companyId}?section=legals`);
        break;
      default:
        break;
    }
  }

  @action
  handlePreviousPage() {
    this.loadCompanies.perform(this.previousPage);
  }

  @action
  handleNextPage() {
    this.loadCompanies.perform(this.nextPage);
  }

  @action
  handleProfileOverlayClose() {
    this.set('application.profileOverlay', null);
  }

  /**
   * Opens the profile overlay.
   *
   * @param {Ember.Component} profileOverlay
   * @param {'user'|'company'} type
   * @param {User|Company} model
   */
  @action
  handleProfileLinkClick(profileOverlay, type, model) {
    this.setProperties({
      profileOverlayType: type,
      profileOverlayModel: model,
      'application.profileOverlay': profileOverlay,
    });
  }
}

export default AdminCompaniesController;
